/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The Ops Console table, for Swift's list pages: a sticky header row (11 px uppercase slate-500),
 * 48 px rows on a CSS grid shared by the header and the rows, hover slate-50, a checkbox column
 * when the list has bulk actions and a trailing "…" row menu.
 *
 * Each list item module exports its columns (ListColumn[]); the page hands the same columns to
 * FilterList, which draws the header, and the item draws its cells with <ListRow>.
 */

import * as React from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";
import { LoaderCircle, MoreHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import Checkbox from "@src/components/ui/Checkbox";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";

export const T9 = {
  slate50: "var(--t9-bg-muted)",
  slate100: "var(--t9-bg-sunken)",
  slate200: "var(--t9-border)",
  slate300: "var(--t9-border-control)",
  slate400: "var(--t9-text-faint)",
  slate500: "var(--t9-text-secondary)",
  slate600: "var(--t9-text-muted)",
  slate700: "var(--t9-text-body)",
  slate800: "var(--t9-text-title2)",
  slate900: "var(--t9-text)",
  brand50: "var(--t9-selected-bg)",
  brand100: "var(--t9-brand-100)",
  brand300: "var(--t9-brand-300)",
  brand500: "var(--t9-brand-500)",
  brand600: "var(--t9-primary)",
  brand700: "var(--t9-primary-hover)",
  accent500: "var(--t9-accent-500)",
};

export type ListColumn = {
  key: string;
  label: string;
  /** A CSS grid track: "minmax(220px, 2fr)", "120px", "1fr"... */
  width: string;
  align?: "left" | "right" | "center";
};

const CHECKBOX_TRACK = "40px";
const ACTIONS_TRACK = "44px";

export const gridTemplate = (columns: ListColumn[], selectable?: boolean) =>
  [selectable ? CHECKBOX_TRACK : null, ...columns.map(c => c.width), ACTIONS_TRACK]
    .filter(Boolean)
    .join(" ");

const rowGrid = css<{ template: string }>`
  display: grid;
  grid-template-columns: ${props => props.template};
  column-gap: 16px;
  align-items: center;
  padding: 0 8px 0 16px;
`;

// ------------------------------------------------------------------ header

const HeaderWrapper = styled.div<{ template: string }>`
  ${rowGrid}
  position: sticky;
  top: 0;
  z-index: 2;
  height: 40px;
  min-height: 40px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
`;
const HeaderCell = styled.div<{ align?: string }>`
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${T9.slate500};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: ${props => props.align || "left"};
`;
const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
`;

type HeaderProps = {
  columns: ListColumn[];
  selectable?: boolean;
  allSelected?: boolean;
  onSelectAllChange?: (checked: boolean) => void;
};
export const ListHeader = (props: HeaderProps) => (
  <HeaderWrapper
    template={gridTemplate(props.columns, props.selectable)}
    data-test-id="listTable-header"
  >
    {props.selectable ? (
      <CheckboxCell>
        <Checkbox
          checked={Boolean(props.allSelected)}
          onChange={checked => props.onSelectAllChange?.(checked)}
        />
      </CheckboxCell>
    ) : null}
    {props.columns.map(c => (
      <HeaderCell key={c.key} align={c.align}>
        {c.label}
      </HeaderCell>
    ))}
    <div />
  </HeaderWrapper>
);

// -------------------------------------------------------------------- rows

const RowWrapper = styled.div<{ template: string; selected?: boolean }>`
  ${rowGrid}
  min-height: 48px;
  border-bottom: 1px solid ${T9.slate100};
  cursor: pointer;
  background: ${props => (props.selected ? T9.brand50 : "var(--t9-bg-surface)")};
  transition: background 150ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    background: ${props => (props.selected ? T9.brand50 : T9.slate50)};
  }
`;

type RowProps = {
  columns: ListColumn[];
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (checked: boolean) => void;
  onClick?: () => void;
  actions?: DropdownAction[];
  /** Drawn in the last column instead of the "…" menu (an expand chevron...). */
  trailing?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
};
export const ListRow = (props: RowProps) => (
  <RowWrapper
    template={gridTemplate(props.columns, props.selectable)}
    selected={props.selected}
    onClick={props.onClick}
    data-test-id={props.testId || "listTable-row"}
    role="row"
  >
    {props.selectable ? (
      <CheckboxCell
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <Checkbox
          checked={props.selected}
          onChange={checked => props.onSelectedChange?.(checked)}
        />
      </CheckboxCell>
    ) : null}
    {props.children}
    <div
      onClick={e => {
        e.stopPropagation();
      }}
    >
      {props.trailing ||
        (props.actions && props.actions.filter(a => !a.hidden).length ? (
          <RowMenu actions={props.actions} />
        ) : null)}
    </div>
  </RowWrapper>
);

// ------------------------------------------------------------------- cells

export const Cell = styled.div<{ align?: string }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 20px;
  color: ${T9.slate700};
  text-align: ${props => props.align || "left"};
`;
export const FlexCell = styled.div<{ gap?: number }>`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${props => (props.gap === undefined ? 8 : props.gap)}px;
  overflow: hidden;
  font-size: 13px;
  color: ${T9.slate700};
`;
export const Stack = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6px 0;
  overflow: hidden;
`;
export const Primary = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${T9.slate900};
`;
export const Secondary = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
`;
export const Muted = styled.span`
  color: ${T9.slate400};
`;
export const Num = styled.div<{ align?: string }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: ${T9.slate700};
  font-variant-numeric: tabular-nums;
  text-align: ${props => props.align || "right"};
`;
export const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  color: ${T9.slate700};
`;

// ---------------------------------------------------------------- pills

export type PillTone = "success" | "danger" | "warning" | "info" | "neutral" | "brand";
const PILL_TONES: { [tone in PillTone]: { bg: string; fg: string } } = {
  success: { bg: "var(--t9-success-50)", fg: "var(--t9-success-700)" },
  danger: { bg: "var(--t9-danger-50)", fg: "var(--t9-danger-700)" },
  warning: { bg: "var(--t9-warning-50)", fg: "var(--t9-warning-700)" },
  info: { bg: "var(--t9-info-50)", fg: "var(--t9-info-700)" },
  neutral: { bg: T9.slate100, fg: T9.slate600 },
  brand: { bg: T9.brand50, fg: T9.brand700 },
};
const PillWrapper = styled.span<{ tone: PillTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${props => PILL_TONES[props.tone].bg};
  color: ${props => PILL_TONES[props.tone].fg};
  svg {
    flex-shrink: 0;
  }
  .spin {
    animation: t9-spin 1s linear infinite;
  }
  @keyframes t9-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
type PillProps = {
  tone: PillTone;
  icon?: LucideIcon;
  spin?: boolean;
  children: React.ReactNode;
  title?: string;
  style?: React.CSSProperties;
};
export const Pill = ({ tone, icon: Icon, spin, children, title, style }: PillProps) => (
  <PillWrapper tone={tone} title={title} style={style} data-test-id="listTable-pill">
    {Icon ? <Icon size={12} strokeWidth={2} className={spin ? "spin" : undefined} /> : null}
    {children}
  </PillWrapper>
);

// Coriolis statuses (transfers, deployments, Swift Agent pools and their machines) as Ops badges.
const STATUS_INFO: { [status: string]: { label: string; tone: PillTone; spin?: boolean } } = {
  COMPLETED: { label: "Completed", tone: "success" },
  RUNNING: { label: "Running", tone: "info", spin: true },
  PENDING: { label: "Pending", tone: "info", spin: true },
  STARTING: { label: "Starting", tone: "info", spin: true },
  ERROR: { label: "Failed", tone: "danger" },
  FAILED_TO_SCHEDULE: { label: "Could not schedule", tone: "danger" },
  FAILED_TO_CANCEL: { label: "Could not cancel", tone: "danger" },
  CANCELED: { label: "Cancelled", tone: "warning" },
  FORCE_CANCELED: { label: "Cancelled", tone: "warning" },
  CANCELED_FOR_DEBUGGING: { label: "Paused for debugging", tone: "warning" },
  CANCELED_AFTER_COMPLETION: { label: "Cancelled", tone: "warning" },
  CANCELLING: { label: "Cancelling", tone: "warning", spin: true },
  CANCELLING_AFTER_COMPLETION: { label: "Cancelling", tone: "warning", spin: true },
  PAUSED: { label: "Paused", tone: "neutral" },
  AWAITING_MINION_ALLOCATIONS: { label: "Awaiting agents", tone: "info", spin: true },
  ERROR_ALLOCATING_MINIONS: { label: "Agent error", tone: "danger" },
  STRANDED_AFTER_DEADLOCK: { label: "Deadlocked", tone: "danger" },
  DEADLOCKED: { label: "Deadlocked", tone: "danger" },
  UNEXECUTED: { label: "Never run", tone: "neutral" },
  SCHEDULED: { label: "Scheduled", tone: "brand" },
  UNSCHEDULED: { label: "Unscheduled", tone: "neutral" },
  // Swift Agent pools
  ALLOCATED: { label: "Allocated", tone: "success" },
  AVAILABLE: { label: "Available", tone: "success" },
  DEALLOCATED: { label: "Deallocated", tone: "neutral" },
  INITIALIZED: { label: "Initialised", tone: "neutral" },
  UNINITIALIZED: { label: "Uninitialised", tone: "neutral" },
  INITIALIZING: { label: "Initialising", tone: "info", spin: true },
  UNINITIALIZING: { label: "Uninitialising", tone: "warning", spin: true },
  VALIDATING_INPUTS: { label: "Validating", tone: "info", spin: true },
  ALLOCATING: { label: "Allocating", tone: "info", spin: true },
  ALLOCATING_SHARED_RESOURCES: { label: "Allocating", tone: "info", spin: true },
  ALLOCATING_MACHINES: { label: "Allocating", tone: "info", spin: true },
  DEALLOCATING: { label: "Deallocating", tone: "warning", spin: true },
  DEALLOCATING_MACHINES: { label: "Deallocating", tone: "warning", spin: true },
  DEALLOCATING_SHARED_RESOURCES: { label: "Deallocating", tone: "warning", spin: true },
  RECONFIGURING: { label: "Reconfiguring", tone: "info", spin: true },
  RESCALING: { label: "Scaling", tone: "info", spin: true },
  SCALING: { label: "Scaling", tone: "info", spin: true },
  IN_MAINTENANCE: { label: "In maintenance", tone: "warning" },
  IN_USE: { label: "In use", tone: "info" },
  DEPLOYING: { label: "Deploying", tone: "info", spin: true },
  HEALTHCHECKING: { label: "Health check", tone: "info", spin: true },
  UNKNOWN: { label: "Unknown", tone: "neutral" },
  // Agent machine power states
  POWERED_ON: { label: "On", tone: "success" }, // On / Off, Starting… / Stopping… (UI spec 8.4)
  POWERED_OFF: { label: "Off", tone: "neutral" },
  POWERING_ON: { label: "Starting…", tone: "info", spin: true },
  POWERING_OFF: { label: "Stopping…", tone: "warning", spin: true },
  POWER_ERROR: { label: "Error", tone: "danger" },
};

export const humanise = (value: string) => {
  const s = value.replace(/[_-]+/g, " ").trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const statusInfo = (status?: string | null) => {
  if (!status) {
    return STATUS_INFO.UNEXECUTED;
  }
  return STATUS_INFO[status] || { label: humanise(status), tone: "neutral" as PillTone };
};

export const StatePill = ({ status, label }: { status?: string | null; label?: string }) => {
  const info = statusInfo(status);
  return (
    <Pill tone={info.tone} icon={info.spin ? LoaderCircle : undefined} spin={info.spin} title={status || undefined}>
      {label || info.label}
    </Pill>
  );
};

// --------------------------------------------------------------- row menu

const MenuButton = styled.button<{ open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: ${T9.slate500};
  background: ${props => (props.open ? T9.slate100 : "transparent")};
  &:hover {
    background: ${T9.slate100};
    color: ${T9.slate900};
  }
  &:focus-visible {
    outline: 2px solid ${T9.accent500};
    outline-offset: 1px;
  }
`;
export const MenuPanel = styled.div`
  position: fixed;
  z-index: 1000;
  min-width: 200px;
  max-height: 360px;
  overflow: auto;
  padding: 4px;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
`;
export const MenuItem = styled.button<{ color?: string; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 6px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  color: ${props => (props.disabled ? T9.slate400 : props.color || T9.slate700)};
  cursor: ${props => (props.disabled ? "default" : "pointer")};
  &:hover {
    background: ${props => (props.disabled ? "transparent" : T9.slate100)};
  }
`;

/** Keeps a floating panel next to its anchor, closes it on outside clicks, scroll and Escape. */
export const usePopover = (align: "left" | "right" = "left") => {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  const place = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const panelWidth = panelRef.current?.offsetWidth || 220;
    const panelHeight = panelRef.current?.offsetHeight || 0;
    let left = align === "right" ? r.right - panelWidth : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));
    let top = r.bottom + 4;
    if (panelHeight && top + panelHeight > window.innerHeight - 8) {
      top = Math.max(8, r.top - panelHeight - 4);
    }
    setPos({ top, left });
  }, [align]);

  React.useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return { open, setOpen, pos, anchorRef, panelRef, place };
};

export const Portal = ({ children }: { children: React.ReactNode }) =>
  typeof document === "undefined" ? null : ReactDOM.createPortal(children, document.body);

type RowMenuProps = {
  actions: DropdownAction[];
  label?: string;
  trigger?: (props: { open: boolean; onClick: () => void; ref: React.Ref<HTMLButtonElement> }) => React.ReactNode;
};
export const RowMenu = ({ actions, label, trigger }: RowMenuProps) => {
  const { open, setOpen, pos, anchorRef, panelRef } = usePopover("right");
  const visible = actions.filter(a => !a.hidden);
  const toggle = () => setOpen(!open);
  return (
    <>
      {trigger ? (
        trigger({ open, onClick: toggle, ref: anchorRef })
      ) : (
        <MenuButton
          ref={anchorRef}
          type="button"
          open={open}
          aria-label={label || "Actions"}
          title={label || "Actions"}
          data-test-id="listTable-rowMenu"
          onClick={e => {
            e.stopPropagation();
            toggle();
          }}
        >
          <MoreHorizontal size={16} />
        </MenuButton>
      )}
      {open ? (
        <Portal>
          <MenuPanel
            ref={panelRef}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            onClick={e => e.stopPropagation()}
            data-test-id="listTable-menu"
          >
            {visible.map(a => (
              <MenuItem
                key={a.label}
                type="button"
                color={a.color}
                disabled={a.disabled || a.loading}
                title={a.title || undefined}
                onClick={() => {
                  if (a.disabled || a.loading) return;
                  setOpen(false);
                  a.action();
                }}
              >
                {a.label}
              </MenuItem>
            ))}
          </MenuPanel>
        </Portal>
      ) : null}
    </>
  );
};

// ------------------------------------------------------------------ dates

const pad = (n: number) => String(n).padStart(2, "0");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Coriolis dates are UTC without a zone ("2026-09-30T10:00:00.000000"). */
export const parseApiDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const hasZone = /[zZ]|[+-]\d\d:?\d\d$/.test(value);
  const d = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "30 Sep 2026, 14:05" (local time). */
export const formatDateTime = (value?: string | null) => {
  const d = parseApiDate(value);
  if (!d) return "";
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/** "30 Sep, 14:05" this year, "30 Sep 2025" before. */
export const formatShortDateTime = (value?: string | null, now = new Date()) => {
  const d = parseApiDate(value);
  if (!d) return "";
  if (d.getFullYear() !== now.getFullYear()) {
    return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const formatDate = (value?: string | null) => {
  const d = parseApiDate(value);
  if (!d) return "";
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** "5 min ago", "3 h ago", "2 d ago", then the date. */
export const timeAgo = (value?: string | null, now = Date.now()) => {
  const d = parseApiDate(value);
  if (!d) return "";
  const s = Math.round((now - d.getTime()) / 1000);
  if (s < 0) return formatDateTime(value);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return formatDate(value);
};

export const pluralise = (n: number, one: string, many?: string) =>
  `${n} ${n === 1 ? one : many || `${one}s`}`;
